import { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Navbar, Nav, Form, Container, NavDropdown, Image } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { IMAGES } from "../../constants/imagePath";
import { logout } from "../../redux/modules/auth/authSlice";
import { setSelectedLocationId } from "../../redux/modules/auth/userSlice";
import { RootState } from "../../redux/rootReducer";
import { ROLE } from "../../utils/enums";
import useAxios from "../../hooks/useAxios";
import { API_ENDPOINTS } from "../../constants/api";
import XLoader from "../loader/XLoader";
import { goToDashboard } from "../../utils/helper";

interface LocationOption {
  value: string;
  label: string;
  isActive: boolean;
}

const Header: FC = () => {
  const dispatch = useDispatch();
  const { orgLocations, first_name, selectedLocationId, role_id, role_parent_id, logoUrl } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { postData, isLoading } = useAxios()

  const isSuperAdminUser =
    [ROLE.SUPER_ADMIN, ROLE.SUPER_ADMIN_STAFF].includes(Number(role_id)) ||
    Number(role_parent_id) === ROLE.SUPER_ADMIN ||
    window.location.pathname.toLowerCase().startsWith('/admin');

  const showLocationDropdown = !isSuperAdminUser && Number(role_id) !== 0 && (orgLocations?.length ?? 0) > 0;

  const locationOptions: LocationOption[] = (orgLocations ?? []).map((v) => ({
    value: String(v.id),
    label: v.display_name || v.address || '',
    isActive: v.is_active !== false,
  }));

  const selectedOption = locationOptions.find((opt) => opt.value === String(selectedLocationId)) ?? null;

  const activeLocation = (orgLocations ?? []).find(
    (loc: any) => String(loc.id) === String(selectedLocationId)
  ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);

  const activeTimezone = activeLocation?.location_timezone || 'UTC';

  const formatLocationTime = (tz: string) => {
    try {
      const now = new Date();
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz || 'UTC',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      return dtf.format(now);
    } catch (e) {
      return '';
    }
  };

  const tzCity = (activeTimezone === 'UTC' || !activeTimezone)
    ? 'UTC'
    : activeTimezone.split('/').pop()?.replace(/_/g, ' ') || 'UTC';

  const [liveClock, setLiveClock] = useState<string>(() => formatLocationTime(activeTimezone));

  useEffect(() => {
    setLiveClock(formatLocationTime(activeTimezone));
    const timer = setInterval(() => {
      setLiveClock(formatLocationTime(activeTimezone));
    }, 1000);
    return () => clearInterval(timer);
  }, [activeTimezone]);

  const handleLocationChange = (option: SingleValue<LocationOption>) => {
    if (!option) return;
    dispatch(setSelectedLocationId(option.value))
    localStorage.setItem('locationId', option.value);
    window.location.reload();
  };

  const handleLogout = async () => {
    const response = await postData(API_ENDPOINTS.LOGOUT);
    if(response.success) {
      dispatch(logout());
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("expandedItems");
      navigate("/login");
    }
  };


  return (
    <Navbar expand="lg" fixed="top">
      {isLoading && <XLoader />}
      <Container fluid>
        <Navbar.Brand >
          <Image 
            src={IMAGES.blueLogo}
            className="cursor-pointer"
            onClick={() => goToDashboard(navigate, role_id)}
          />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="navbarScroll" />
        <Navbar.Collapse id="navbarScroll">
          <Nav className="ms-auto my-2 my-lg-0" navbarScroll>
            <Form className="d-flex align-items-center nav-options">
              <div className="location-profile d-flex align-items-center">
                {!isSuperAdminUser && activeLocation && liveClock && (
                  <div
                    className="d-flex align-items-center me-4"
                    style={{
                      fontSize: '13px',
                      color: '#475467',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.2',
                    }}
                  >
                    <span className="me-2 fw-medium">{liveClock}</span>
                    <span
                      style={{
                        backgroundColor: '#EBECEF',
                        border: '1px solid #D0D5DD',
                        borderRadius: '8px',
                        color: '#344054',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        lineHeight: '14px',
                        display: 'inline-block',
                      }}
                    >
                      {tzCity}
                    </span>
                  </div>
                )}
                {showLocationDropdown && (
                  <Form.Group
                    controlId="formBasicEmail"
                    className="d-flex align-items-center select-location"
                  >
                    <Form.Label className="d-flex align-items-center gap-1">
                      Location
                    </Form.Label>
                    <Select
                      classNamePrefix="location-select"
                      options={locationOptions}
                      value={selectedOption}
                      onChange={handleLocationChange}
                      isSearchable
                      placeholder="Select location"
                      menuPortalTarget={document.body}
                      formatOptionLabel={(option: LocationOption) => (
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <span>{option.label}</span>
                          {!option.isActive && (
                            <span
                              style={{
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '1px 6px',
                                marginLeft: '8px',
                              }}
                            >
                              Inactive
                            </span>
                          )}
                        </div>
                      )}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minWidth: 200,
                          borderColor: "transparent",
                          height: "45px"
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </Form.Group>
                )}
                <div className="profile d-flex align-items-center">
                  <Image
                    height={45}
                    width={45}
                    src={logoUrl && !isSuperAdminUser ? logoUrl : IMAGES.profile}
                    style={{ borderRadius: "50%" }}
                  />
                  <NavDropdown
                    title={first_name}
                    id="navbarScrollingDropdown"
                    align="end"
                  >
                    {!isSuperAdminUser && (
                      <>
                        <NavDropdown.Item onClick={() => {
                          localStorage.setItem('expandedItems', JSON.stringify([]));
                          navigate('/org/settings/profile')
                        }}>
                        Profile
                        </NavDropdown.Item>
                        <NavDropdown.Divider />
                      </>
                    )}
                    <NavDropdown.Item onClick={() => handleLogout()}>
                      Logout
                    </NavDropdown.Item>
                  </NavDropdown>
                </div>
              </div>
            </Form>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;